
import Vue from 'vue';
import { PropType } from 'vue';
import GlassButton from '@/components/base/GlassButton.vue';
import DropdownToggle from '@/components/base/DropdownToggle.vue';
import ImageGalleryFilterMenu from '@/components/ImageGallery/ImageGalleryFilterMenu.vue';
import RunnitResult from '../../views/Runnits/RunnitResult.vue';
import RunnitImageInfoCarouselDialog from '../../views/Runnits/RunnitImageInfoCarouselDialog.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import NoImagesEmptyState from '@/assets/NoImagesEmptyState.vue';
import {
	RunnitNodeRun,
	RunnitNodeRunResult,
	RunnitUpload,
	Avatar,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RUNNIT_NODE_STATIC_FIELDS_KEY,
	RUNNIT_NODE_RUN_RESULT_TYPE,
	THUMB_SIZE,
	RUNNIT_NODE_RUN_STATE,
	RunnitNodeDef,
} from '@run-diffusion/shared';
import _debounce from 'lodash/debounce';
import { mapActions, mapGetters, mapState } from 'vuex';
import { SELECTED_COLUMNS_MAP } from '@/views/Runnits/constants';
import _differenceBy from 'lodash/differenceBy';
import _mapKeys from 'lodash/mapKeys';
import _unionBy from 'lodash/unionBy';
import _range from 'lodash/range';
import { getRandomLoadingQuip } from '@/components/states/LoadingState/utils';
import _get from 'lodash/get';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';
import { ResultFavoritesMixin } from '@/mixins/ResultFavoritesMixin';
import {
	getRunnitsLibraryFilterSelection,
	RUNNIT_LIBRARY_FILTER_LOCATION,
	setRunnitsLibraryFilterSelection,
} from '@/utils';
import {
	createGallerySelectionRenderContext,
	GALLERY_SELECTION_CONTEXT_KEY,
	updateGallerySelectionRenderContext,
} from '@/components/ImageGallery/gallerySelectionContext';
import {
	LIBRARY_SELECT_ALL_MAX_RESULTS,
	buildLibrarySelectionSnapshot,
	drainLibrarySelectionPages,
} from '@/utils/libraryBulkSelection';

interface ImageInfoCarouselConfig {
	dialogOpen: boolean;
	nodeRun: RunnitNodeRun | null;
	nodeRunResult: RunnitNodeRunResult | null;
}

interface PlaceholderResult extends RunnitNodeRunResult {
	isPlaceholder?: boolean;
}

interface VirtualRow {
	id: number;
	results: PlaceholderResult[];
	size: number;
	top: number;
}

export default Vue.extend({
	name: 'VirtualScrollResults',
	provide () {
		return {
			[GALLERY_SELECTION_CONTEXT_KEY]: this.gallerySelectionContext,
		};
	},
	props: {
		fetchPageFunction: {
			type: Function as PropType<(lastDoc: any, pageSize: number, selectedFilter: string | null) => Promise<{ results: RunnitNodeRun[], hasMore: boolean, lastDoc: any, failed?: boolean }>>,
		},
		facetFiltersActive: { type: Boolean, default: false },
		pageSize: {
			type: [Number, String],
			default: 10,
		},
		height: {
			type: [Number, String],
			default: THUMB_SIZE,
		},
		paddingX: {
			type: String,
			default: '16px',
		},
		paddingTop: {
			type: Number,
			default: 24,
		},
		bench: {
			type: Number,
			default: 3,
		},
		disableCarousel: {
			type: Boolean,
			default: false,
		},
		resultFilter: {
			type: Function as PropType<(result: RunnitNodeRunResult) => boolean>,
			default: () => true,
		},
		backgroundColor: {
			type: String,
			default: 'transparent',
		},
		gap: {
			type: Number,
			default: 16,
		},
		draggable: {
			type: Boolean,
			default: false,
		},
		showTrainingResults: {
			type: Boolean,
			default: false,
		},
		hideToolName: {
			type: Boolean,
			default: false,
		},
		stickyLoadMore: {
			type: Boolean,
			default: false,
		},
		filterOptions: {
			type: Array as PropType<{
				label: string;
				value: string;
				icon: string;
			}[]>,
			default: () => [],
		},
		nodeDef: {
			type: [Object, String] as PropType<RunnitNodeDef | string>,
			default: null,
		},
		toolOnboardingTopCardDismissed: {
			type: Boolean,
			default: false,
		},
		filterLocalStorageKey: {
			type: String,
			default: RUNNIT_LIBRARY_FILTER_LOCATION.RUNNITS_LIBRARY,
		},
		virtualScrollContentStyles: {
			type: Object,
			default: null,
		},
	},
	data () {
		return {
			SELECTED_COLUMNS_MAP: Object.freeze(SELECTED_COLUMNS_MAP),
			useThumbs: true,
			initializing: false,
			isLoadingMore: false,
			lastDoc: null as any,
			hasMoreResults: true,
			runnitNodeRunsById: {} as Record<string, RunnitNodeRun>,
			results: [] as PlaceholderResult[],
			containerWidth: 0,
			imageInfoCarouselConfig: {
				dialogOpen: false,
				nodeRun: null,
				nodeRunResult: null,
			} as ImageInfoCarouselConfig,
			scrollTop: 0,
			visibleRange: {
				start: 0,
				end: 0,
			},
			rowHeights: new Map<number, number>(),
			observer: null as ResizeObserver | null,
			currentLoadingQuip: getRandomLoadingQuip(),
			scrollableHeaderHeight: 0,
			filterToggleHeight: 0,
			selectedFilter: null as string | null,
			// Cache for expensive computations
			cachedRows: [] as VirtualRow[],
			lastResultsLength: 0,
			lastNumColumns: 0,
			lastUseThumbs: true,
			gallerySelectionContext: createGallerySelectionRenderContext(),
			selectAllRequestVersion: 0,
			pageStoreVersion: 0,
			pageLoadPromise: null as Promise<'success' | 'failed' | 'skipped'> | null,
			// Latched by a failed page so infinite scroll stops re-firing it. A failed
			// fetch keeps hasMoreResults true (the set is not exhausted, it is
			// unanswered), and handleScroll fires on every scroll event past the
			// threshold, so without this latch one dead page becomes a request storm
			// and a snackbar per attempt. Cleared by an explicit "Load More" retry or
			// any page-store reset.
			pageLoadFailed: false,
		};
	},
	mixins: [
		TeamUserMixin,
		ResultFavoritesMixin,
	],
	computed: {
		...mapState([
			'runnitState',
			'user',
		]),
		...mapGetters([
			'currentSelectionState',
		]),
		numColumns (): number {
			// SELECTED_COLUMNS_MAP is a display-label map, so its values are strings.
			if (!this.$vuetify.breakpoint.smAndUp) return 2;
			return Number(SELECTED_COLUMNS_MAP[this.runnitState?.selectedColumns]) || 4;
		},
		rows (): VirtualRow[] {
			// Always recalculate if no cache or if cache is empty
			if (!this.cachedRows.length) {
				this.calculateRows();
				return this.cachedRows;
			}

			// Check if we need to recalculate rows based on significant changes
			const shouldRecalculate =
				this.results.length !== this.lastResultsLength ||
				this.numColumns !== this.lastNumColumns ||
				this.useThumbs !== this.lastUseThumbs;

			if (shouldRecalculate) {
				this.calculateRows();
			}

			return this.cachedRows;
		},
		visibleRows (): VirtualRow[] {
			return this.rows.slice(this.visibleRange.start, this.visibleRange.end);
		},
		totalHeight (): number {
			return this.rows.reduce((sum, row) => sum + row.size + this.gap, 0);
		},
		runnitNodeRuns (): RunnitNodeRun[] {
			return (this.results || []).map(result => this.runnitNodeRunsById[result.nodeRunId]).filter(Boolean);
		},
		columnWidth (): number {
			if (['vw', '%'].some(unit => this.paddingX.includes(unit))) {
				console.error('paddingX only supports px units')
			}
			const paddingX = parseFloat(this.paddingX) || 0;
			return (this.containerWidth - (this.gap * (this.numColumns - 1)) - (paddingX * 2)) / this.numColumns;
		},
		firstFilterOption (): { label: string; value: string; icon: string } | null {
			return this.filterOptions[0] || null;
		},
		structuredFilterOptions () {
			// Convert the old array structure to the new object structure
			const featuredOption = this.filterOptions.find(option => option.value === 'FEATURED');
			const dropdownOptions = this.filterOptions.filter(option => option.value !== 'FEATURED');

			return {
				featured: featuredOption,
				dropdown: dropdownOptions,
			};
		},
	},
	watch: {
		currentSelectionState: {
			immediate: true,
			handler (newVal) {
				updateGallerySelectionRenderContext(this.gallerySelectionContext, newVal);
			},
		},
		filterOptions: {
			handler (newVal: { value: string }[]) {
				if (!newVal || !newVal.length) return;
				// External filter changes (e.g. RunnitWorkflow's per-node toggle) shrink filterOptions
				// to a single option. Sync selectedFilter and refetch, since onFilterChange is only
				// driven by the in-gallery dropdown.
				if (newVal.length === 1 && this.selectedFilter !== newVal[0].value) {
					this.onFilterChange(newVal[0].value);
					return;
				}
				// The host can also withdraw an option that is currently selected. The
				// Library drops TEAM when the account switches away from an eligible
				// team, and `state.team` keeps the previous team after that switch, so
				// leaving TEAM selected would keep reading a team the caller is no
				// longer acting as. Falling back does NOT persist: the withdrawal is
				// the account's doing, not a choice, and overwriting the stored
				// selection would lose TEAM for the next visit under that team.
				if (!newVal.some((option) => option.value === this.selectedFilter)) {
					this.onFilterChange(newVal[0].value, { persist: false });
				}
			},
		},
		favoritesByResultUuid: {
			handler (newVal: Map<string, any>) {
				// FAVORITES view stays in sync with the global favorites bind: unfavorited results
				// drop from the displayed list immediately, and newly favorited results (e.g. heart
				// clicked from another gallery or the carousel) are pulled in at the top.
				if (this.selectedFilter !== 'FAVORITES') return;

				const filtered = this.results.filter((r: any) => !r?.uuid || newVal.has(r.uuid));
				if (filtered.length !== this.results.length) {
					const survivingNodeRunIds = new Set(filtered.map((r: any) => r.nodeRunId));
					this.results = filtered;
					Object.keys(this.runnitNodeRunsById).forEach((nodeRunId: string) => {
						if (!survivingNodeRunIds.has(nodeRunId)) {
							delete this.runnitNodeRunsById[nodeRunId];
						}
					});
					this.cachedRows = [];
				}

				const currentUuids = new Set(this.results.map((r: any) => r?.uuid).filter(Boolean));
				const hasNewFavorite = Array.from(newVal.keys()).some((uuid: string) => !currentUuids.has(uuid));
				if (hasNewFavorite) {
					this.pullInNewlyFavoritedResults();
				}
			},
		},
		numColumns: {
			handler (newVal: number, oldVal: number) {
				if (newVal !== oldVal) {
					// Invalidate cached rows when column count changes
					this.cachedRows = [];

					// Use requestAnimationFrame to batch DOM updates
					requestAnimationFrame(() => {
						if (this.$refs.virtualScrollContainer) {
							(this.$refs.virtualScrollContainer as HTMLElement).scrollTop = 0;
						}
						this.scrollTop = 0;
						this.updateVisibleRange();
					});
				}
			}
		},
		'runnitState.deletedImages.nodeRunResults': {
			handler (newVal: RunnitNodeRunResult[], oldVal: RunnitNodeRunResult[]) {
				if (newVal.length && _differenceBy(newVal || [], oldVal || [], 'uuid').length) {
					const deletedNodeRunResultsMap: Record<string, RunnitNodeRunResult> = _mapKeys(newVal, 'uuid');
					const deletedNsfwPlaceholderNodeRunIds = new Set(
						newVal
							.filter((result: RunnitNodeRunResult) => result?.isNsfw && result?.uuid?.startsWith(`placeholder-${result.nodeRunId}-`))
							.map((result: RunnitNodeRunResult) => result.nodeRunId)
					);

					// Filter out deleted results from the results array
					this.results = this.results.filter(result => !deletedNodeRunResultsMap[result.uuid] && !deletedNsfwPlaceholderNodeRunIds.has(result.nodeRunId));

					// Update nodeRunsById to remove empty results
					Object.keys(this.runnitNodeRunsById).forEach(nodeRunId => {
						if (deletedNsfwPlaceholderNodeRunIds.has(nodeRunId)) {
							delete this.runnitNodeRunsById[nodeRunId];
							return;
						}

						const nodeRun = this.runnitNodeRunsById[nodeRunId];
						nodeRun.results = (nodeRun.results || []).filter(result => !deletedNodeRunResultsMap[result.uuid]);
						if (nodeRun.deletedAt) {
							delete this.runnitNodeRunsById[nodeRunId];
						}
					});

					// Invalidate cached rows
					this.cachedRows = [];
				}
			}
		},
		'runnitState.deletedImages.uploads': {
			handler (newVal: RunnitUpload[]) {
				if (newVal.length) {
					const deletedUploadsMap: Record<string, RunnitUpload> = _mapKeys(newVal, 'id');
					this.results = this.results.filter(result => !deletedUploadsMap[result.uploadId]);
					this.cachedRows = [];
				}
			}
		},
		'runnitState.deletedImages.avatars': {
			handler (newVal: Avatar[]) {
				if (newVal.length) {
					const deletedAvatarsMap: Record<string, Avatar> = _mapKeys(newVal, 'id');
					this.results = this.results.filter(result => !deletedAvatarsMap[result.avatarId]);
					this.cachedRows = [];
				}
			}
		},
		'runnitState.settingsDrawerOpen': {
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal !== oldVal) {
					// Wait for drawer animation to complete (300ms is typical Vuetify drawer animation duration)
					setTimeout(() => {
						this.$nextTick(() => {
							this.updateContainerWidth();
						});
					}, 300);
				}
			}
		},
		initializing (newVal: boolean, oldVal: boolean) {
			if (newVal && !oldVal) {
				this.currentLoadingQuip = getRandomLoadingQuip();
			}
			if (!newVal && oldVal) {
				// When initializing finishes, measure the header height
				this.$nextTick(() => {
					setTimeout(() => {
						this.updateScrollableHeaderHeight();
						this.updateFilterToggleHeight();
					}, 50);
				});
			}
		},
		'toolOnboardingTopCardDismissed': {
			handler (newVal: Record<string, boolean>, oldVal: Record<string, boolean>) {
				this.$nextTick(() => {
					setTimeout(() => {
						this.updateScrollableHeaderHeight();
						this.updateFilterToggleHeight();
					}, 50);
				});
			}
		},
	},
	mounted () {
		this.$nextTick(() => {
			setTimeout(() => {
				this.updateContainerWidth();
				this.updateScrollableHeaderHeight();
				this.updateFilterToggleHeight();
			}, 300);
		});
		window.addEventListener('resize', this.updateContainerWidth);
		window.addEventListener('resize', this.debouncedUpdateHeaderHeight);
		window.addEventListener('resize', this.debouncedUpdateFilterToggleHeight);
		this.setupResizeObserver();
	},
	beforeDestroy () {
		this.cancelSelectAllResults();
		this.invalidatePageLoad();
		window.removeEventListener('resize', this.updateContainerWidth);
		window.removeEventListener('resize', this.debouncedUpdateHeaderHeight);
		window.removeEventListener('resize', this.debouncedUpdateFilterToggleHeight);
		if (this.observer) {
			this.observer.disconnect();
		}
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		calculateRows () {
			// Early return if no results to avoid unnecessary work
			if (!this.results || !this.results.length) {
				this.cachedRows = [];
				this.lastResultsLength = 0;
				return;
			}

			const rows: VirtualRow[] = [];
			let currentRow: VirtualRow = { id: 1, results: [], size: 0, top: 0 };
			let currentTop = 0;
			const columnWidth = this.columnWidth; // Cache this value

			// Pre-filter deleted results to avoid checking in the loop
			const activeResults = this.results.filter(result => !result.deletedAt);

			activeResults.forEach((result, index) => {
				currentRow.results.push(result);

				if (currentRow.results.length >= this.numColumns || index === activeResults.length - 1) {
					const rowHeight = currentRow.results.reduce((maxHeight, result) => {
						// LAYERS carries its display dimensions on displayFile; ASSET_3D
						// previews are always a square THUMB_SIZE (handled just below).
						let fileWidth, fileHeight;
						if (result.type === RUNNIT_NODE_RUN_RESULT_TYPE.LAYERS && result.displayFile) {
							fileWidth = result.displayFile.width;
							fileHeight = result.displayFile.height;
						} else if (result.file) {
							fileWidth = result.file.width;
							fileHeight = result.file.height;
						}

						if (result.type === RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D && (!fileWidth || !fileHeight)) {
							fileWidth = THUMB_SIZE;
							fileHeight = THUMB_SIZE;
						}

						if (!fileWidth || !fileHeight) return maxHeight;
						const aspectRatio = fileWidth / fileHeight;

						// Apply max width/height of THUMB_SIZE
						const maxDimension: number = THUMB_SIZE;
						let width: number = columnWidth;
						let height: number = width / aspectRatio;

						// If either dimension exceeds the max, scale down proportionally
						if (this.useThumbs && (width > maxDimension || height > maxDimension)) {
							if (width > height) {
								width = maxDimension;
								height = width / aspectRatio;
							} else {
								height = maxDimension;
								width = height * aspectRatio;
							}
						}

						return Math.max(Math.ceil(maxHeight), Math.ceil(height));
					}, 0);

					currentRow.size = rowHeight || 150;
					currentRow.top = currentTop;
					rows.push(currentRow);

					currentTop += currentRow.size + this.gap;
					currentRow = { id: currentRow.id + 1, results: [], size: 0, top: 0 };
				}
			});

			// Update cache with current state
			this.cachedRows = Object.freeze(rows);
			this.lastResultsLength = this.results.length;
			this.lastNumColumns = this.numColumns;
			this.lastUseThumbs = this.useThumbs;
		},
		debouncedUpdateLayout: _debounce(function () {
			this.updateContainerWidth();
		}, 300),

		debouncedUpdateHeaderHeight: _debounce(function () {
			this.updateScrollableHeaderHeight();
		}, 100),

		debouncedUpdateFilterToggleHeight: _debounce(function () {
			this.updateFilterToggleHeight();
		}, 100),
		visibleResultsForNodeRuns (runnitNodeRuns: RunnitNodeRun[]): PlaceholderResult[] {
			return runnitNodeRuns.reduce((acc, nodeRun) => {
				if (!this.showTrainingResults && nodeRun?.nodeDef?.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER) {
					return acc;
				}
				let filteredResults = (nodeRun.results || []).filter(result => this.resultFilter(result));
				if (nodeRun.state === RUNNIT_NODE_RUN_STATE.NSFW && !filteredResults.length && !nodeRun.deletedAt) {
					const length: number = this._get(nodeRun, `staticInputs[${RUNNIT_NODE_STATIC_FIELDS_KEY.numResults}]`) || 1;
					filteredResults = _range(length).map(i => ({
						uuid: `placeholder-${nodeRun.id}-${i}`,
						nodeRunId: nodeRun.id,
						type: RUNNIT_NODE_RUN_RESULT_TYPE.IMG,
						isNsfw: true,
						file: { width: THUMB_SIZE, height: THUMB_SIZE },
					})) as PlaceholderResult[];
				}
				return _unionBy(acc, filteredResults, 'uuid');
			}, [] as PlaceholderResult[]);
		},
		cancelSelectAllResults (): void {
			this.selectAllRequestVersion += 1;
		},
		invalidatePageLoad (): void {
			this.pageStoreVersion += 1;
			this.pageLoadPromise = null;
			this.isLoadingMore = false;
			this.pageLoadFailed = false;
		},
		// The "Load More" button is the explicit retry after a failed page: it
		// clears the latch so a success resumes scroll-driven paging.
		retryLoadNextPage (): Promise<'success' | 'failed' | 'skipped'> {
			this.pageLoadFailed = false;
			return this.loadNextPage();
		},
		async collectAllSelectableResults (canSelectTypes: string[], triggeringId: string) {
			if (!this.fetchPageFunction) return { status: 'failed' as const };
			const requestVersion = ++this.selectAllRequestVersion;
			const selectedFilter = this.selectedFilter;
			const isStale = (): boolean => (
				requestVersion !== this.selectAllRequestVersion ||
				this.selectedFilter !== selectedFilter ||
				this.currentSelectionState?.triggeringId !== triggeringId ||
				!this.currentSelectionState?.nodeRunResultSelectionMode
			);

			const collected = await drainLibrarySelectionPages({
				hasMore: () => this.hasMoreResults,
				cursor: () => this.lastDoc,
				loadNextPage: () => this.loadNextPage(),
				isStale,
				// `results` is already the flattened, type-filtered display list, so
				// it is the count the ceiling is about. The Library excludes TEXT and
				// MODEL server side, so loaded and selectable are the same set here.
				loadedCount: () => this.results.length,
				maxResults: LIBRARY_SELECT_ALL_MAX_RESULTS,
			});
			if (collected.status !== 'success') return collected;

			const snapshot = buildLibrarySelectionSnapshot(
				this.results,
				this.runnitNodeRunsById,
				canSelectTypes,
				LIBRARY_SELECT_ALL_MAX_RESULTS,
			);
			return {
				status: 'success' as const,
				...snapshot,
				// Two ways to fall short of the whole filtered set: the drain stopped
				// with pages left, or the last page overshot the ceiling and the
				// snapshot trimmed it. Either one is worth telling the user about.
				truncated: collected.truncated || snapshot.truncated,
			};
		},

		debouncedHandleResize: _debounce(function (entries: ResizeObserverEntry[]) {
			let shouldUpdateVisibleRange = false;
			let shouldUpdateContainer = false;

			// Batch all changes to reduce DOM reads/writes
			requestAnimationFrame(() => {
				entries.forEach(entry => {
					// Check if this is the scrollableHeader element
					if (entry.target === this.$refs.scrollableHeader) {
						this.updateScrollableHeaderHeight();
						return;
					}

					// Check if this is the filterToggleContainer element
					if (entry.target === this.$refs.filterToggleContainer) {
						this.updateFilterToggleHeight();
						return;
					}

					const rowId = parseInt(entry.target.getAttribute('data-row-id') || '0');
					const newHeight = entry.contentRect.height;
					if (this.rowHeights.get(rowId) !== newHeight) {
						this.rowHeights.set(rowId, newHeight);
						shouldUpdateVisibleRange = true;
					}

					shouldUpdateContainer = true;
				});

				// Batch updates
				if (shouldUpdateContainer) {
					this.updateContainerWidth();
					this.debouncedUpdateHeaderHeight();
				}

				if (shouldUpdateVisibleRange) {
					this.updateVisibleRange();
				}
			});
		}, 200),

		setupResizeObserver () {
			if (this.observer) {
				this.observer.disconnect();
			}

			this.observer = new ResizeObserver((entries) => {
				this.debouncedHandleResize(entries);
			});

			// Observe the wrapper element for container size changes
			if (this.$refs.virtualScrollWrapper) {
				this.observer.observe(this.$refs.virtualScrollWrapper as Element);
			}

			// Observe the scrollable header for height changes
			if (this.$refs.scrollableHeader) {
				this.observer.observe(this.$refs.scrollableHeader as Element);
			}

			// Observe the filter toggle container for height changes
			if (this.$refs.filterToggleContainer) {
				this.observer.observe(this.$refs.filterToggleContainer as Element);
			}
		},
		updateVisibleRange () {
			const containerHeight = this.$el?.clientHeight || 800;
			const scrollTop = this.scrollTop;
			const scrollBottom = scrollTop + containerHeight;

			// Find the first row that starts after the top of the viewport
			const startIndex = Math.max(0, this.rows.findIndex(row => row.top >= scrollTop));

			// Find the first row that starts after the bottom of the viewport
			let endIndex = this.rows.findIndex(row => row.top >= scrollBottom);

			// If we're at the bottom, show all remaining rows
			if (endIndex === -1) {
				endIndex = this.rows.length;
			} else {
				endIndex = Math.min(this.rows.length, endIndex + 1);
			}

			// Add bench rows above and below
			const rowsAbove = Math.max(0, startIndex - this.bench);
			const rowsBelow = Math.min(this.rows.length, endIndex + this.bench);

			this.visibleRange = {
				start: rowsAbove,
				end: rowsBelow,
			};
		},
		onScroll: _debounce(function (e: Event) {
			const target = e.target as HTMLElement;
			const newScrollTop = target.scrollTop;

			// Reduce threshold back to 10px to make scrolling more responsive
			if (Math.abs(newScrollTop - this.scrollTop) > 10) {
				this.scrollTop = newScrollTop;
				// Use requestAnimationFrame for smooth updates
				requestAnimationFrame(() => {
					this.updateVisibleRange();
				});
			}

			// Check if we need to load more - infinite scroll detection
			const remainingScroll = target.scrollHeight - target.scrollTop - target.clientHeight;
			if (remainingScroll < 700 && this.hasMoreResults && !this.isLoadingMore && !this.pageLoadFailed) {
				this.loadNextPage();
			}
		}, 16),
		updateContainerWidth () {
			if (this.$el) {
				// Use requestAnimationFrame to batch DOM reads
				requestAnimationFrame(() => {
					const rect = this.$el.getBoundingClientRect();
					const newWidth = rect.width - 32;
					if (Math.abs(this.containerWidth - newWidth) > 1) { // Only update if difference is significant
						this.containerWidth = newWidth;
						// Invalidate cached rows when width changes
						this.cachedRows = [];
						this.updateVisibleRange();
					}
				});
			}
		},
		updateScrollableHeaderHeight () {
			if (this.$refs.scrollableHeader) {
				const newHeight = (this.$refs.scrollableHeader as HTMLElement).clientHeight || 0;
				if (this.scrollableHeaderHeight !== newHeight) {
					this.scrollableHeaderHeight = newHeight;
				}
			}
		},
		updateFilterToggleHeight () {
			if (this.$refs.filterToggleContainer) {
				const newHeight = ((this.$refs.filterToggleContainer as HTMLElement).clientHeight || 0) + 20; // 20px is the margin-top of the filter toggle container
				if (this.filterToggleHeight !== newHeight) {
					this.filterToggleHeight = newHeight;
				}
			}
		},
		async loadNextPage (): Promise<'success' | 'failed' | 'skipped'> {
			if (this.pageLoadPromise) return this.pageLoadPromise;
			if (!this.hasMoreResults || !this.fetchPageFunction) return 'skipped';
			if (this.selectedFilter === 'TEAM' && !this.team) return 'skipped'; // wait for the team to finish loading
			const pageStoreVersion = this.pageStoreVersion;
			const selectedFilter = this.selectedFilter;
			const cursor = this.lastDoc;

			const pageLoad = (async (): Promise<'success' | 'failed'> => {
				try {
					const response = await this.fetchPageFunction(cursor, this.pageSize, selectedFilter);
					if (
						pageStoreVersion !== this.pageStoreVersion ||
						selectedFilter !== this.selectedFilter
					) return 'success';
					if (response.failed) return 'failed';
					const { results: runnitNodeRuns, hasMore, lastDoc } = response;

					this.hasMoreResults = hasMore;
					this.lastDoc = lastDoc;

					this.runnitNodeRunsById = {
						...this.runnitNodeRunsById,
						...runnitNodeRuns.reduce((acc, run) => {
							acc[run.id] = run;
							return acc;
						}, {} as Record<string, RunnitNodeRun>)
					};

					const resultList = this.visibleResultsForNodeRuns(runnitNodeRuns);

					this.results = [...this.results, ...resultList];

					// Invalidate cached rows when new results are added
					this.cachedRows = [];

					this.$nextTick(() => {
						this.updateVisibleRange();
						this.updateScrollableHeaderHeight();
						this.updateFilterToggleHeight();
					});
					return 'success';
				} catch (error) {
					console.error('Failed to fetch page:', error);
					return 'failed';
				}
			})();
			this.pageLoadPromise = pageLoad;
			this.isLoadingMore = true;
			try {
				const outcome = await pageLoad;
				if (this.pageLoadPromise === pageLoad) {
					this.pageLoadFailed = (outcome === 'failed');
				}
				return outcome;
			} finally {
				if (this.pageLoadPromise === pageLoad) {
					this.pageLoadPromise = null;
					this.isLoadingMore = false;
				}
			}
		},
		async pullInNewlyFavoritedResults () {
			if (this.selectedFilter !== 'FAVORITES' || !this.fetchPageFunction) return;
			try {
				const response = await this.fetchPageFunction(null, this.pageSize, 'FAVORITES');
				const newNodeRuns: RunnitNodeRun[] = response?.results || [];

				const existingUuids: Set<string> = new Set(this.results.map((r: PlaceholderResult) => r?.uuid).filter(Boolean));
				const newResultsToPrepend: PlaceholderResult[] = [];
				const newNodeRunsToCache: Record<string, RunnitNodeRun> = {};

				for (const nodeRun of newNodeRuns) {
					if (!this.showTrainingResults && nodeRun?.nodeDef?.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER) {
						continue;
					}
					const filteredResults: RunnitNodeRunResult[] = (nodeRun.results || []).filter(this.resultFilter);
					const trulyNew: RunnitNodeRunResult[] = filteredResults.filter((r: RunnitNodeRunResult) => r?.uuid && !existingUuids.has(r.uuid));
					if (trulyNew.length) {
						newResultsToPrepend.push(...trulyNew);
						if (!this.runnitNodeRunsById[nodeRun.id]) {
							newNodeRunsToCache[nodeRun.id] = nodeRun;
						}
					}
				}

				if (!newResultsToPrepend.length) return;

				this.runnitNodeRunsById = { ...this.runnitNodeRunsById, ...newNodeRunsToCache };
				this.results = [...newResultsToPrepend, ...this.results];
				this.cachedRows = [];
				this.$nextTick(() => {
					this.updateVisibleRange();
				});
			} catch (err) {
				console.error('pullInNewlyFavoritedResults failed', err);
			}
		},
		onNodeRunResultClick (nodeRun: RunnitNodeRun, result: RunnitNodeRunResult): void {
			if (!this.disableCarousel) {
				this.imageInfoCarouselConfig = {
					dialogOpen: !!(nodeRun && result),
					nodeRun,
					nodeRunResult: result,
				};
			}
			this.$emit('on-node-run-result-click', { nodeRun, result });
		},
		handleIsPublicUpdated (payload: { nodeRunResult: RunnitNodeRunResult; nodeRun: RunnitNodeRun }): void {
			this.imageInfoCarouselConfig.nodeRun = { ...payload.nodeRun };
			this.imageInfoCarouselConfig.nodeRunResult = { ...payload.nodeRunResult };
		},
		handleCarouselStep ({ nodeRunResult, nodeRun }): void {
			if (!this.imageInfoCarouselConfig.dialogOpen) return;

			this.imageInfoCarouselConfig = {
				dialogOpen: !!(nodeRun && nodeRunResult),
				nodeRun,
				nodeRunResult,
			};
			const INFINITE_LOAD_BUFFER = 4;
			if (this.results.findIndex(result => result.uuid === nodeRunResult.uuid) > this.results.length - INFINITE_LOAD_BUFFER) {
				this.loadNextPage();
			}
		},
		hasPlaceholderResults (nodeRun: RunnitNodeRun): boolean {
			return this.results.some(result => result.nodeRunId === nodeRun.id && result.isPlaceholder);
		},
		addPlaceholderRun (nodeRun: RunnitNodeRun) {
			// Filtered views are REST-owned (ADR 0018): do not inject realtime
			// placeholders. The guard is deliberately INSIDE the method so the
			// Favorites auto-switch in the tail runs either way — callers must not
			// skip this call while filtered.
			if (!this.facetFiltersActive && !this.hasPlaceholderResults(nodeRun)) {
				const length: number = this._get(nodeRun, `staticInputs[${RUNNIT_NODE_STATIC_FIELDS_KEY.numResults}]`) || 1;
				const placeholderResults = _range(length).map(i => ({
					uuid: `placeholder-${nodeRun.id}-${i}`,
					nodeRunId: nodeRun.id,
					isPlaceholder: true,
					// Add a fixed height for placeholders to ensure consistent row sizing
					// This is used to calculate the aspect ratio. THUMB_SIZE x THUMB_SIZE is the thumbnail size
					file: {
						width: THUMB_SIZE,
						height: THUMB_SIZE
					}
				})) as PlaceholderResult[];

				// Check for existing placeholders with the same UUID to prevent duplicates
				const existingUuids = new Set(this.results.map(r => r.uuid));
				const newPlaceholders = placeholderResults.filter(placeholder => !existingUuids.has(placeholder.uuid));

				// Add placeholders at the beginning
				this.results = [...newPlaceholders, ...this.results];

				// Force a visible range update to ensure all placeholders are visible
				this.$nextTick(() => {
					this.updateVisibleRange();
				});
			}
			this.switchToPersonalFilterForNewGeneration();
		},
		addNewRun (nodeRun: RunnitNodeRun) {
			// Filtered views are REST-owned (ADR 0018): the paged REST list is
			// authoritative, so do not splice realtime results into it.
			if (this.facetFiltersActive) return;

			// Add nodeRun to the map
			this.runnitNodeRunsById = {
				[nodeRun.id]: nodeRun,
				...this.runnitNodeRunsById
			};

			// Add results to the results array
			if (nodeRun.results && nodeRun.results.length) {
				const filteredResults = nodeRun.results.filter(this.resultFilter);

				// Check if we have placeholder results for this nodeRun
				const hasPlaceholders = this.hasPlaceholderResults(nodeRun);

				if (hasPlaceholders) {
					// Remove all placeholders for this nodeRun first
					this.results = this.results.filter(result =>
						!(result.nodeRunId === nodeRun.id && result.isPlaceholder)
					);

					// Then add the actual results at the beginning, filtering out any duplicates
					const existingUuids = new Set(this.results.map(r => r.uuid));
					const newResults = filteredResults.filter(result => !existingUuids.has(result.uuid));
					this.results = [...newResults, ...this.results];
				} else {
					// If no placeholders exist, add new results at the beginning, filtering out any duplicates
					const existingUuids = new Set(this.results.map(r => r.uuid));
					const newResults = filteredResults.filter(result => !existingUuids.has(result.uuid));
					this.results = [...newResults, ...this.results];
				}
			} else if (nodeRun.state === RUNNIT_NODE_RUN_STATE.NSFW && !nodeRun.results?.length) {
				this.results = this.results.map(result => {
					if (result.nodeRunId === nodeRun.id && result.isPlaceholder) {
						return {
							...result,
							type: RUNNIT_NODE_RUN_RESULT_TYPE.IMG,
							isPlaceholder: false,
							isNsfw: true,
						};
					}
					return result;
				});
			}

			// Invalidate cached rows when new data is added
			this.cachedRows = [];
		},
		removeNodeRun (nodeRun: RunnitNodeRun) {
			delete this.runnitNodeRunsById[nodeRun.id];
			this.results = this.results.filter(result => result.nodeRunId !== nodeRun.id);
		},
		onSingleSelect (result: { nodeRunResult: RunnitNodeRunResult, upload: RunnitUpload, avatar: Avatar }) {
			this.$emit('on-node-run-result-single-select', this.runnitNodeRunsById[result.nodeRunResult.nodeRunId], result.nodeRunResult);
		},
		onMultiSelect (result: { nodeRunResult: RunnitNodeRunResult, upload: RunnitUpload, avatar: Avatar }) {
			this.$emit('on-node-run-result-multi-select', this.runnitNodeRunsById[result.nodeRunResult.nodeRunId], result.nodeRunResult);
		},
		onFilterChange (value: string, options?: { persist?: boolean }) {
			this.cancelSelectAllResults();
			this.invalidatePageLoad();
			this.selectedFilter = value;
			// Don't persist FEATURED or FAVORITES — both are read-only views
			// that would land a returning user on an empty default.
			if (options?.persist !== false && this.selectedFilter !== 'FEATURED' && this.selectedFilter !== 'FAVORITES') {
				setRunnitsLibraryFilterSelection(this.filterLocalStorageKey, this.selectedFilter);
			}

			// Reset component state for new filter
			this.results = [];
			this.runnitNodeRunsById = {};
			this.lastDoc = null;
			this.hasMoreResults = true;
			this.isLoadingMore = false;
			this.pageLoadFailed = false;

			// Reset scroll position
			if (this.$refs.virtualScrollContainer) {
				(this.$refs.virtualScrollContainer as HTMLElement).scrollTop = 0;
			}
			this.scrollTop = 0;
			this.updateVisibleRange();

			// Reload data with new filter
			this.loadNextPage();

			this.$emit('on-filter-change', value);
		},
		async toggleOnboardingDismissed (nodeDefId: string) {
			this.$emit('toggle-onboarding-dismissed', nodeDefId);
		},
		async determineInitialFilter () {
			// If only one filter option is available, use that one
			if (this.filterOptions.length === 1) {
				this.selectedFilter = this.filterOptions[0].value;
				return;
			}
			// Check local storage for last selected filter
			let storedFilter = getRunnitsLibraryFilterSelection(this.filterLocalStorageKey);
			if (this.filterOptions.find(option => option.value === storedFilter)) {
				this.selectedFilter = storedFilter;
				return;
			}
			// The probe below asks whether the caller has any results at all. Under
			// an active filter it would run the filtered request instead: one more
			// request per remount, and a second error when that request fails.
			// Filters only show on ALL or TEAM (TEAM is restored above), so stay on ALL.
			if (this.facetFiltersActive) {
				this.selectedFilter = 'ALL';
				return;
			}

			// Check if we have personal results by trying to fetch a small page
			try {
				const personalResponse = await this.fetchPageFunction(null, 1, 'THIS_TOOL');
				const hasPersonalResults = personalResponse.results.length > 0;

				if (hasPersonalResults) {
					// If we have personal results, set the initial filter to ALL
					this.selectedFilter = 'ALL';
				} else {
					// If no personal results, try to set to featured if available
					const featuredOption = this.filterOptions.find(option => option.value === 'FEATURED');
					if (featuredOption) {
						this.selectedFilter = 'FEATURED';
					} else {
						// Fallback to ALL if no featured option available
						this.selectedFilter = 'ALL';
					}
				}
			} catch (error) {
				console.error('Error determining initial filter:', error);
				// Fallback to ALL filter
				this.selectedFilter = 'ALL';
			}
		},
		switchToPersonalFilterForNewGeneration () {
			// FEATURED and FAVORITES are read-only views — a brand-new generation
			// belongs in a personal filter, not in either of those.
			if (this.selectedFilter === 'FEATURED' || this.selectedFilter === 'FAVORITES') {
				const firstDropdownOption = this.structuredFilterOptions.dropdown[0];
				if (firstDropdownOption) {
					this.onFilterChange(firstDropdownOption.value);
				}
			}
		},
		resetComponentForReinitialize () {
			// Only reset loading state and cache, but keep existing results
			// This preserves completed node runs while allowing fresh data loading
			this.lastDoc = null;
			this.hasMoreResults = true;
			this.isLoadingMore = false;
			this.pageLoadFailed = false;
			this.cachedRows = [];
			this.lastResultsLength = 0;
			this.lastNumColumns = 0;
			this.lastUseThumbs = true;

			// Don't reset scroll position when reinitializing to maintain user's view
			this.updateVisibleRange();
		},
		onTeamGenerationsFiltersChange (filters: { mediaFilters: Record<string, boolean>, dateRangeFilters: Record<string, string>, toolFilters: Record<string, boolean>, userFilters: Record<string, string> }) {
			this.$emit('on-team-generations-filters-change', filters);
		},
	},
	async created () {
		try {
			this.initializing = true;

			// Determine initial filter before loading data
			await this.determineInitialFilter();

			// Surface the resolved/restored scope so the Library page can gate
			// its facet toolbar on first render (localStorage may restore TEAM/
			// FAVORITES; otherwise the page would see a stale default of ALL).
			this.$emit('on-filter-change', this.selectedFilter);

			await this.loadNextPage();

			// Force initial visible range update after first page load
			this.$nextTick(() => {
				// Add a small delay to ensure the onboarding card has fully rendered
				// to its actual content height, not just its max-height
				setTimeout(() => {
					this.updateVisibleRange();
					this.updateScrollableHeaderHeight();
					this.updateFilterToggleHeight();
				}, 100);
			});
		} catch (error) {
			console.error('Failed to load initial data:', error);
		} finally {
			this.initializing = false;
		}
	},
	components: {
		GlassButton,
		DropdownToggle,
		RunnitResult,
		RunnitImageInfoCarouselDialog,
		EmptyState,
		NoImagesEmptyState,
		ImageGalleryFilterMenu,
	},
});
